// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { useCreditStore } from '@/app/stores/credit-store';

const CREDITS_QUERY_KEY = ['credits'] as const;
const OPERATION_PRICES_QUERY_KEY = ['operation-prices'] as const;

type PackId = 'pack_2' | 'pack_5' | 'pack_10';

/**
 * Lightweight hook that fetches and syncs the credit balance into the
 * Zustand store. Meant for always-mounted components like the sidebar
 * so the balance stays fresh regardless of which page the user is on.
 */
export function useCreditBalance() {
  const setBalance = useCreditStore((s: { setBalance: (b: string) => void }) => s.setBalance);

  return useQuery({
    queryKey: [...CREDITS_QUERY_KEY, 'balance'],
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/api/v1/credits', {
        params: { query: { limit: 1, offset: 0 } },
      });

      if (error) {
        throw new Error(error.error.message);
      }

      // Sync store so other components see it immediately.
      if (data.data.balance) {
        setBalance(data.data.balance);
      }

      return data.data.balance;
    },
    staleTime: 30 * 1000, // Re-fetch at most every 30 seconds.
  });
}

/** Hook for fetching credit balance and transaction history. */
export function useCredits(limit = 20, offset = 0) {
  return useQuery({
    queryKey: [...CREDITS_QUERY_KEY, limit, offset],
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/api/v1/credits', {
        params: { query: { limit, offset } },
      });

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
  });
}

/** Hook for fetching operation prices. */
export function useOperationPrices() {
  return useQuery({
    queryKey: OPERATION_PRICES_QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/api/v1/credits/prices');

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    staleTime: 5 * 60 * 1000, // Prices don't change often — cache for 5 minutes.
  });
}

/** Hook for purchasing credits via Stripe Checkout. */
export function usePurchaseCredits() {
  return useMutation({
    mutationFn: async ({ packId }: { packId: PackId }) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/credits/purchase',
        {
          body: { pack_id: packId },
        },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
  });
}

/** Hook for requesting a credit refund. */
export function useRefundCredits() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data, error } = await apiClient.POST(
        '/api/v1/credits/refund',
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      // Invalidate credits to refresh balance and transaction history.
      queryClient.invalidateQueries({ queryKey: CREDITS_QUERY_KEY });
    },
  });
}
